/*
 * Profile-wizard org configuration — canon §5.2 genderPolicy + §5.3 eventFields.
 *
 * Both axes are org-configured per event (canon §10 variant matrix). The DC
 * exposes them as props (genderPolicy default `binary`; eventFields default
 * `Trumin defaults (phone optional, no address)`) — NEVER hardcoded in the
 * form itself.
 *
 * // TODO(collision-D): reconcile canon eventFields presets with settings
 * // participantConfig. The shipped settings-owned contract
 * // (src/data/participantConfig.ts DataCollectionResolved: per-field
 * // {key, collect} booleans, no address field, no genderPolicy, no
 * // optional-vs-required distinction) cannot express the canon eventFields
 * // model below. Escalated to boardroom (collision D). Until settled, the
 * // wizard is built to the CANON structure behind this seam, and an
 * // unresolved/unspecified config resolves to the canon defaults
 * // (genderPolicy binary; Trumin defaults — phone optional, no address).
 */
import type { DataCollectionResolved } from '../../data/participantConfig'

/** Canon §5.2: org gender policy. */
export type GenderPolicy = 'binary' | 'flagged' | 'third'

/** false = never rendered (no "(optional)" filler — canon §11.4). */
export type FieldMode = false | 'optional' | 'required'

export interface WizardEventFields {
  address: FieldMode
  phone: FieldMode
}

export interface WizardConfig {
  genderPolicy: GenderPolicy
  eventFields: WizardEventFields
  /** Organiser display name for policy notices/helpboxes — [demo] `Apex
   *  Games` in the DC; production comes with the event's org record. */
  organiser?: string
}

/** Canon §5.3 eventFields presets — names verbatim from the DC prop enum. */
export const EVENT_FIELDS_PRESETS: Record<string, WizardEventFields> = {
  'Trumin defaults (phone optional, no address)': { address: false, phone: 'optional' },
  'Address required by organiser': { address: 'required', phone: 'optional' },
  'Address optional': { address: 'optional', phone: 'optional' },
  'Phone required': { address: false, phone: 'required' },
  'Minimum — no address or phone': { address: false, phone: false },
}

export const DEFAULT_EVENT_FIELDS: WizardEventFields =
  EVENT_FIELDS_PRESETS['Trumin defaults (phone optional, no address)']

export const DEFAULT_WIZARD_CONFIG: WizardConfig = {
  genderPolicy: 'binary',
  eventFields: DEFAULT_EVENT_FIELDS,
}

/**
 * Resolve the wizard config from the settings participant-config seam.
 * See TODO(collision-D) above: the resolved data_collection shape carries
 * neither genderPolicy nor address nor optional/required — so today every
 * resolvable input maps to the canon defaults. The parameter is kept so the
 * call-sites are already wired for the reconciled contract.
 */
export function resolveWizardConfig(resolved: DataCollectionResolved | null): WizardConfig {
  void resolved // no expressible signal yet — see TODO(collision-D)
  return DEFAULT_WIZARD_CONFIG
}
